import { useEffect, useState } from 'react';
import {
  Settings as SettingsIcon, Sun, Moon, Bell, Mail, Award, FileText, LogOut,
  ArrowLeft, User, Eye, Zap, Map as MapIcon, Bot, Car, ChevronRight, Megaphone,
  Languages, Cake, Volume2, BellRing, BellOff,
} from 'lucide-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@evi/skins';
import {
  APP_VERSION_LABEL,
  SUPPORTED_LOCALES, LOCALE_LABELS, LOCALE_FLAG, normalizeLocale, voiceOptionsForLocale,
  type SupportedLocale,
} from '@evi/shared';
import { useVoicePreview } from '@/hooks/useVoicePreview';
import { useAuthStore } from '@/store/auth';
import { useMapPrefsStore } from '@/store/mapPrefs';
import { useEvieVoiceStore } from '@/store/evieVoice';
import { useLocaleStore } from '@/store/locale';
import {
  fetchAccount, logout, logoutEverywhere, updatePrivacy,
  fetchPreferences, updatePreferences, fetchBirthday,
  type AccountView, type PreferencesView,
} from '@/lib/api';
import {
  pushSupport, notificationPermission, currentSubscription, enablePush, disablePush,
} from '@/lib/push';
import { toast } from '@/store/toast';
import { PageHeader } from '@/components/PageHeader';
import NamedIcon from '@/components/ui/NamedIcon';
import { InstallAppButton } from '@/components/InstallAppButton';
import { PhoneCaptureModal } from '@/components/ui/PhoneCaptureModal';
import { BirthdayCaptureModal } from '@/components/ui/BirthdayCaptureModal';
import { formatToDisplay } from '@/lib/phone-format';
import { MarketingEmailControls } from './MarketingEmailControls';
import styles from './SettingsPage.module.css';

interface Props { onNavigate: (path: string) => void }

export function SettingsPage({ onNavigate }: Props) {
  const { t, i18n } = useTranslation('settings');
  const setLocale = useLocaleStore((s) => s.setLocale);
  const activeLocale = normalizeLocale(i18n.language);
  const user = useAuthStore((s) => s.user);
  // EVII voice — per-driver choice (female | male), default female. Persisted in
  // the evieVoice store keyed by user id; sent to the speak endpoint at play time.
  const voiceChoice = useEvieVoiceStore(
    (s) => (user?._id ? s.voiceByUser[user._id] ?? 'female' : 'female'),
  );
  const setVoiceChoice = useEvieVoiceStore((s) => s.setVoiceChoice);
  // Data-driven voice options for the active language (unit 4): [] hides the
  // whole voice section (text-only, e.g. Armenian); one entry (Chinese) shows a
  // single voice with no gender toggle. A single-option locale resolves female.
  const voiceOpts = voiceOptionsForLocale(activeLocale);
  const effectiveVoice = voiceOpts.includes(voiceChoice) ? voiceChoice : (voiceOpts[0] ?? 'female');
  // Tapping a voice plays its sample clip; tapping another cuts the first
  // off; unmounting (navigating away) stops playback. No autoplay.
  const { play: playSample, playing: previewing } = useVoicePreview();
  // Mic auto-start on EVII open (per-user, persisted; default on).
  const micAutostart = useEvieVoiceStore(
    (s) => (user?._id ? s.micAutostartByUser[user._id] ?? true : true),
  );
  const setMicAutostart = useEvieVoiceStore((s) => s.setMicAutostart);
  // Device-local cosmetic map preference (persists across sessions via the
  // zustand persist store — independent of the account-level /me/preferences).
  const showClusterStatusRing = useMapPrefsStore((s) => s.showClusterStatusRing);
  const setShowClusterStatusRing = useMapPrefsStore((s) => s.setShowClusterStatusRing);
  const { theme, mode, setMode } = useTheme();
  // Single-mode families (availableModes set + the opposite mode not in
  // it) can't be toggled — the provider would clamp the flip straight
  // back — so disable the control and skip the flip rather than show a
  // dead toggle. Absent availableModes (every family today) = both modes,
  // so the toggle works exactly as before.
  const otherMode = mode === 'dark' ? 'light' : 'dark';
  const canToggleMode = !theme.availableModes || theme.availableModes.includes(otherMode);
  const toggleMode = (): void => {
    if (!canToggleMode) return;
    setMode(otherMode);
  };
  const qc = useQueryClient();

  // Real server-persisted privacy state. Sits on the account
  // doc alongside disclaimer flags + phone (the same place
  // /me/account already serves). React Query cache shared
  // with the rest of the PWA so a toggle here updates any
  // surface that reads `discoverable_by_username`.
  const accountQ = useQuery({
    queryKey: ['account'],
    queryFn: fetchAccount,
    staleTime: 60_000,
  });
  // Phone editor — moved here from the Profile menu's Account sheet.
  // Same PhoneCaptureModal flow and copy; only the entry point moved.
  const [phoneEditorOpen, setPhoneEditorOpen] = useState(false);
  // Birthday (set-once) — editable row when unset, read-only once locked.
  const [birthdayEditorOpen, setBirthdayEditorOpen] = useState(false);
  const birthdayQ = useQuery({
    queryKey: ['birthday'],
    queryFn: fetchBirthday,
    staleTime: 60_000,
  });
  const privacyMut = useMutation({
    mutationFn: updatePrivacy,
    // Optimistic flip + rollback on error. The toggle should
    // feel instant; the network round-trip is the silent
    // guarantor, not the visible state-flip.
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: ['account'] });
      const prev = qc.getQueryData(['account']);
      qc.setQueryData(['account'], (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        return { ...(old as Record<string, unknown>), ...patch };
      });
      return { prev };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.prev) qc.setQueryData(['account'], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['account'] }),
  });

  // Phase 5a Item 11 — notification + alert prefs now server-
  // persisted via /me/preferences. Same optimistic pattern as
  // privacyMut above.
  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: fetchPreferences,
    staleTime: 60_000,
  });
  const prefsMut = useMutation({
    mutationFn: updatePreferences,
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: ['preferences'] });
      const prev = qc.getQueryData<PreferencesView>(['preferences']);
      qc.setQueryData(['preferences'], (old: PreferencesView | undefined) => {
        if (!old) return old;
        return { ...old, ...patch };
      });
      return { prev };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.prev) qc.setQueryData(['preferences'], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['preferences'] }),
  });
  // Marketing categories: the server decides the resulting record (turning
  // the last category off is an unsubscribe), so take its answer rather than
  // guessing optimistically.
  const marketingMut = useMutation({
    mutationFn: updatePreferences,
    onSuccess: (data) => { qc.setQueryData(['preferences'], data); },
    onError: () => { toast(t('error', { ns: 'emailPreferences' }), { tone: 'warn' }); },
  });

  const handleLogout = async () => {
    if (!confirm(t('signOut.confirm'))) return;
    await logout();
    onNavigate('/login');
  };

  // Sign out signs out this device only; this one ends every session the
  // account holds (both apps, every device), for a lost or shared phone.
  const [signingOutEverywhere, setSigningOutEverywhere] = useState(false);
  const handleLogoutEverywhere = async () => {
    if (!confirm(t('signOutEverywhere.confirm'))) return;
    setSigningOutEverywhere(true);
    const done = await logoutEverywhere();
    setSigningOutEverywhere(false);
    if (!done) { toast(t('signOutEverywhere.failed'), { tone: 'warn' }); return; }
    onNavigate('/login');
  };

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => onNavigate('/profile')}>
        <ArrowLeft size={16} /> {t('back')}
      </button>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        iconSlot="settings"
        iconFallback={SettingsIcon}
      />

      {/* Profile info */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}><User size={14} /> {t('profile.title')}</h2>
        <div className={styles.row}>
          <span className={styles.rowLabel}>{t('profile.name')}</span>
          <span className={styles.rowValue}>{user?.name ?? '—'}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowLabel}>{t('profile.email')}</span>
          <span className={styles.rowValue}>{user?.email ?? '—'}</span>
        </div>
        {/* Phone: tappable, unlike the read-only rows above — the number is
            editable through the same PhoneCaptureModal the Profile menu
            used before the row moved here. */}
        <button className={styles.toggleRow} onClick={() => setPhoneEditorOpen(true)}>
          <span>{t('profile.phone')}</span>
          <span className={styles.toggleValue}>
            {accountQ.data?.phone ? formatToDisplay(accountQ.data.phone) : t('profile.addPhone')}
            {' '}<ChevronRight size={14} />
          </span>
        </button>
        {/* Birthday — set once, then locked (read-only). Editable row when
            unset; a static read-only row with a "contact support" hint once set. */}
        {birthdayQ.data?.locked ? (
          <div className={styles.row}>
            <span className={styles.rowLabel}>{t('profile.birthday')}</span>
            <span className={styles.rowValueCol}>
              <span className={styles.rowValue}>
                {birthdayQ.data.birthday
                  ? new Date(2000, birthdayQ.data.birthday.month - 1, birthdayQ.data.birthday.day)
                    .toLocaleDateString(i18n.language, { month: 'long', day: 'numeric' })
                  : '—'}
              </span>
              <span className={styles.rowHint}>{t('profile.birthdayLockedHint')}</span>
            </span>
          </div>
        ) : (
          <button className={styles.toggleRow} onClick={() => setBirthdayEditorOpen(true)}>
            <span><Cake size={14} /> {t('profile.birthday')}</span>
            <span className={styles.toggleValue}>
              {t('profile.addBirthday')} <ChevronRight size={14} />
            </span>
          </button>
        )}
        <div className={styles.row}>
          <span className={styles.rowLabel}>{t('profile.role')}</span>
          <span className={styles.rowValue}>{user?.role ?? '—'}</span>
        </div>
      </div>

      {/* My EV — moved here from the My Charging sheet (Residential UX
          slice A): vehicle identity belongs with profile preferences.
          Route /vehicle unchanged; only the menu home moved. */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}><Car size={14} /> {t('vehicle.title')}</h2>
        <button className={styles.toggleRow} onClick={() => onNavigate('/vehicle')}>
          <span>{t('vehicle.myEv')}</span>
          <span className={styles.toggleValue}><ChevronRight size={14} /></span>
        </button>
      </div>

      {/* Appearance */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{mode === 'dark' ? <Moon size={14} /> : <Sun size={14} />} {t('appearance.title')}</h2>
        <button
          className={styles.toggleRow}
          onClick={toggleMode}
          disabled={!canToggleMode}
        >
          <span>{t('appearance.mode')}</span>
          <span className={styles.toggleValue}>
            {mode === 'dark' ? t('appearance.dark') : t('appearance.light')}
            {canToggleMode ? t('appearance.tapToSwitch') : ''}
          </span>
        </button>
      </div>

      {/* Language — i18n. A native select for the 8 supported UI languages;
          the choice is saved to the account and follows the driver across
          devices. Legal copy stays English regardless. */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}><Languages size={14} /> {t('language.title')}</h2>
        <p className={styles.voiceDesc}>{t('language.desc')}</p>
        <select
          className={styles.langSelect}
          aria-label={t('language.title')}
          value={activeLocale}
          onChange={(e) => setLocale(user?._id ?? null, e.target.value as SupportedLocale)}
        >
          {SUPPORTED_LOCALES.map((loc) => (
            // Flag prepended to the option TEXT (not a separate node): emoji in
            // native <select> options render cross-platform this way.
            <option key={loc} value={loc}>{`${LOCALE_FLAG[loc]}  ${LOCALE_LABELS[loc]}`}</option>
          ))}
        </select>
      </div>

      {/* EVII voice — per-driver voice for spoken answers in voice mode.
          Data-driven off the active language: personas (The Guide / The
          Navigator) surface instead of Polly names; a single-voice language
          (Chinese) shows one option with no gender toggle; a text-only
          language (Armenian) hides the whole section, mic toggle included. */}
      {voiceOpts.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}><Bot size={14} /> {t('voice.title')}</h2>
          <p className={styles.voiceDesc}>{t('voice.desc')}</p>
          <div className={styles.voiceSeg} role="radiogroup" aria-label={t('voice.ariaLabel')}>
            {voiceOpts.map((opt) => (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={effectiveVoice === opt}
                className={`${styles.voiceSegBtn} ${effectiveVoice === opt ? styles.voiceSegBtnActive : ''}`}
                onClick={() => {
                  setVoiceChoice(user?._id ?? null, opt);
                  // Hear it before committing. `opt` is what the picker
                  // actually offers for this language, so a single-voice
                  // locale (ko/zh) previews its one voice rather than a
                  // persona it has no clip for. Playback only ever starts
                  // from this tap — never on open.
                  playSample(activeLocale, opt);
                }}
              >
                {t(`voice.persona.${opt}`)}
                {previewing === `${activeLocale}-${opt}` && (
                  <Volume2 size={13} className={styles.voiceSegPlaying} aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
          {/* Mic auto-start on open (per-user, persisted; default on). */}
          <p className={styles.voiceDesc}>{t('voice.micDesc')}</p>
          <div className={styles.voiceSeg} role="radiogroup" aria-label={t('voice.micAriaLabel')}>
            {([true, false] as const).map((on) => (
              <button
                key={String(on)}
                type="button"
                role="radio"
                aria-checked={micAutostart === on}
                className={`${styles.voiceSegBtn} ${micAutostart === on ? styles.voiceSegBtnActive : ''}`}
                onClick={() => setMicAutostart(user?._id ?? null, on)}
              >
                {on ? t('voice.on') : t('voice.off')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Map */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}><MapIcon size={14} /> {t('map.title')}</h2>
        <NotifToggle
          icon={MapIcon}
          label={t('map.label')}
          desc={t('map.desc')}
          checked={showClusterStatusRing}
          onChange={setShowClusterStatusRing}
        />
      </div>

      {/* Privacy */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}><Eye size={14} /> {t('privacy.title')}</h2>
        <NotifToggle
          icon={Eye}
          label={t('privacy.label')}
          desc={t('privacy.desc')}
          checked={accountQ.data?.discoverable_by_username === true}
          disabled={accountQ.isLoading || privacyMut.isPending}
          onChange={(v) => privacyMut.mutate({ discoverable_by_username: v })}
        />
      </div>

      {/* Charger alerts — only for users with at least one resident-
          managed charger. */}
      {prefsQ.data?.has_resident_chargers && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}><Zap size={14} /> {t('chargerAlerts.title')}</h2>
          <NotifToggle
            icon={Zap} label={t('chargerAlerts.label')}
            desc={t('chargerAlerts.desc')}
            checked={prefsQ.data?.resident_alert_opt_in === true}
            disabled={prefsQ.isLoading || prefsMut.isPending}
            onChange={(v) => prefsMut.mutate({ resident_alert_opt_in: v })}
          />
        </div>
      )}

      {/* Push notifications — the DEVICE grant plus the per-event
          toggles. Separate section from Email below, and deliberately
          above it: push is the channel a driver actually feels. */}
      <PushNotificationsSection
        prefs={prefsQ.data}
        loading={prefsQ.isLoading}
        saving={prefsMut.isPending}
        onChange={(patch) => prefsMut.mutate(patch)}
      />

      {/* Email notifications */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}><Bell size={14} /> {t('emailNotifications.title')}</h2>
        <NotifToggle
          icon={Mail} label={t('emailNotifications.sessionComplete.label')}
          desc={t('emailNotifications.sessionComplete.desc')}
          checked={prefsQ.data?.notify_session_complete === true}
          disabled={prefsQ.isLoading || prefsMut.isPending}
          onChange={(v) => prefsMut.mutate({ notify_session_complete: v })}
        />
        <NotifToggle
          icon={Bell} label={t('emailNotifications.paymentFailed.label')}
          desc={t('emailNotifications.paymentFailed.desc')}
          checked={prefsQ.data?.notify_payment_failed === true}
          disabled={prefsQ.isLoading || prefsMut.isPending}
          onChange={(v) => prefsMut.mutate({ notify_payment_failed: v })}
        />
        <NotifToggle
          icon={Award} label={t('emailNotifications.badgeEarned.label')}
          desc={t('emailNotifications.badgeEarned.desc')}
          checked={prefsQ.data?.notify_badge_earned === true}
          disabled={prefsQ.isLoading || prefsMut.isPending}
          onChange={(v) => prefsMut.mutate({ notify_badge_earned: v })}
        />
        <NotifToggle
          icon={FileText} label={t('emailNotifications.weeklyReport.label')}
          desc={t('emailNotifications.weeklyReport.desc')}
          checked={prefsQ.data?.notify_weekly_report === true}
          disabled={prefsQ.isLoading || prefsMut.isPending}
          onChange={(v) => prefsMut.mutate({ notify_weekly_report: v })}
        />
      </div>

      {/* Marketing email (23 October 2026): enrolled by default, three
          categories each on until the driver turns it off, and unsubscribe
          from all. The same controls as the public page an email links to.
          The server-side send gate reads this record at send time. Distinct
          from the transactional Email notifications above. */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}><Megaphone size={14} /> {t('marketing.title')}</h2>
        <MarketingEmailControls
          value={prefsQ.data?.marketing}
          disabled={prefsQ.isLoading || marketingMut.isPending}
          onToggle={(category, on) => marketingMut.mutate({ marketing_categories: { [category]: on } })}
          onUnsubscribeAll={() => marketingMut.mutate({ marketing_unsubscribe_all: true })}
        />
      </div>

      {/* Install app — fires the Android/desktop install prompt, or shows an
          iOS Share hint. Renders nothing when already installed or when no
          install path is available. */}
      <InstallAppButton />

      {/* Sign out */}
      <button className={styles.logoutBtn} onClick={handleLogout}>
        <NamedIcon name="logout" fallback={<LogOut size={16} />} size={16} /> {t('signOut.label')}
      </button>
      <button
        className={`${styles.logoutBtn} ${styles.logoutAllBtn}`}
        onClick={() => { void handleLogoutEverywhere(); }}
        disabled={signingOutEverywhere}
      >
        {t('signOutEverywhere.label')}
      </button>

      <p className={styles.versionNote}>{APP_VERSION_LABEL}</p>

      {phoneEditorOpen && (
        <PhoneCaptureModal
          title={accountQ.data?.phone ? t('phoneModal.titleUpdate') : t('phoneModal.titleAdd')}
          reason={t('phoneModal.reason')}
          onCapture={async (phone) => {
            qc.setQueryData<AccountView | undefined>(['account'], (prev) =>
              prev ? { ...prev, phone } : prev,
            );
            setPhoneEditorOpen(false);
          }}
          onSkip={() => setPhoneEditorOpen(false)}
        />
      )}

      {birthdayEditorOpen && (
        <BirthdayCaptureModal
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['birthday'] });
            setBirthdayEditorOpen(false);
          }}
          onClose={() => setBirthdayEditorOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Push section: the device grant, then the per-event toggles.
 *
 * The two-level shape is the point. A per-event toggle is meaningless
 * until the OS has granted permission AND this device is registered, so
 * the toggles stay hidden until the grant exists — otherwise a driver
 * flips "Charging started" on, receives nothing, and concludes the app
 * is broken.
 *
 * Every state a browser can be in gets its own copy:
 *   ready + granted + subscribed → toggles, plus a way to turn the
 *     device off again
 *   ready + default              → the enable button (a user gesture,
 *     which is what requestPermission requires)
 *   denied                       → we cannot re-ask; only browser
 *     settings can undo it, so say that instead of offering a button
 *     that would do nothing
 *   needs-install (iOS Safari)   → Add to Home Screen first
 *   unsupported                  → render nothing at all
 */
function PushNotificationsSection({
  prefs, loading, saving, onChange,
}: {
  prefs: PreferencesView | undefined;
  loading: boolean;
  saving: boolean;
  onChange: (patch: Partial<PreferencesView>) => void;
}) {
  const { t } = useTranslation('settings');
  const support = pushSupport();
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    () => notificationPermission(),
  );
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    void currentSubscription().then((s) => { if (alive) setSubscribed(s !== null); });
    return () => { alive = false; };
  }, []);

  if (support === 'unsupported') return null;

  const enable = async () => {
    setBusy(true);
    try {
      const result = await enablePush();
      setPermission(notificationPermission());
      if (result === 'enabled') {
        setSubscribed(true);
        toast(t('push.enabled'), { tone: 'accent' });
      } else if (result === 'denied') {
        toast(t('push.deniedToast'), { tone: 'warn' });
      } else if (result === 'server-error') {
        toast(t('push.error'), { tone: 'warn' });
      }
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disablePush();
      setSubscribed(false);
      toast(t('push.disabled'), { tone: 'neutral' });
    } finally {
      setBusy(false);
    }
  };

  const active = permission === 'granted' && subscribed === true;

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}><BellRing size={14} /> {t('push.title')}</h2>

      {support === 'needs-install' ? (
        <p className={styles.notifDesc} style={{ padding: '4px 2px' }}>{t('push.needsInstall')}</p>
      ) : permission === 'denied' ? (
        <p className={styles.notifDesc} style={{ padding: '4px 2px' }}>{t('push.blocked')}</p>
      ) : !active ? (
        <>
          <p className={styles.notifDesc} style={{ padding: '4px 2px 10px' }}>{t('push.pitch')}</p>
          <button
            type="button"
            className={styles.notifRow}
            disabled={busy}
            onClick={() => { void enable(); }}
            style={{
              width: '100%', background: 'none', textAlign: 'left', font: 'inherit', color: 'inherit',
              ...(busy ? { opacity: 0.6, pointerEvents: 'none' } : {}),
            }}
          >
            <div className={styles.notifIcon}><BellRing size={16} /></div>
            <div className={styles.notifInfo}>
              <p className={styles.notifLabel}>{t('push.enableCta')}</p>
            </div>
            <ChevronRight size={16} />
          </button>
        </>
      ) : (
        <>
          {/* ── The seven rows ────────────────────────────────────
              Grain is per-CATEGORY, not per-type: a new notification
              type declares a category and lands in an existing row,
              so this page does not grow as the type list does.
              Order is by consequence, most costly to miss first. */}

          {/* Account: no switch, by design. Not knowing costs money. */}
          <div className={styles.notifRow} style={{ display: 'block' }}>
            <p className={styles.notifLabel}>{t('push.account.label')}</p>
            <p className={styles.notifDesc}>{t('push.account.desc')}</p>
          </div>

          <ModeRow
            label={t('push.charging.label')}
            desc={t('push.charging.desc')}
            modes={['all', 'finish_only', 'off']}
            current={prefs?.push_charging ?? 'all'}
            optionLabel={(m) => t(`push.charging.${m}`)}
            disabled={loading || saving}
            onPick={(m) => onChange({ push_charging: m as 'all' | 'finish_only' | 'off' })}
          />

          <NotifToggle
            icon={Award} label={t('push.social.label')} desc={t('push.social.desc')}
            checked={prefs?.push_social === true}
            disabled={loading || saving}
            onChange={(v) => onChange({ push_social: v })}
          />
          <NotifToggle
            icon={Zap} label={t('push.rewards.label')} desc={t('push.rewards.desc')}
            checked={prefs?.push_rewards === true}
            disabled={loading || saving}
            onChange={(v) => onChange({ push_rewards: v })}
          />

          {/* News & offers is CONSENT, not a preference. It is off until
              the driver turns it on in their own words, its copy names
              this channel rather than email, and it is deliberately not
              wired to marketing_opt_in — that consent asked about
              email, and consent is sought separately per channel. */}
          <NotifToggle
            icon={Bell} label={t('push.newsOffers.label')} desc={t('push.newsOffers.desc')}
            checked={prefs?.push_news_offers === true}
            disabled={loading || saving}
            onChange={(v) => onChange({ push_news_offers: v })}
          />

          {/* Host rows. Only the host changes the mode: the system
              offers once (a bell row after three busy days in seven)
              and never flips it on their behalf. */}
          {prefs?.has_resident_chargers && (
            <ModeRow
              label={t('push.hostMode.label')}
              desc={t('push.hostMode.desc')}
              modes={['each', 'daily', 'off']}
              current={prefs?.push_host_mode ?? 'each'}
              optionLabel={(m) => t(`push.hostMode.${m}`)}
              disabled={loading || saving}
              onPick={(m) => onChange({ push_host_mode: m as 'each' | 'daily' | 'off' })}
            />
          )}

          {/* Alerts is NOT gated on hosting. The alert audience is
              tenant admins + tenant.alert_subscribers + the resident
              owner, and a grant-based admin is role 'driver' on this
              very app — so gating this row on has_resident_chargers
              hid the only mute from most of the people who actually
              receive these pushes. A driver with no chargers simply
              never has content for it. */}
          <NotifToggle
            icon={FileText} label={t('push.alerts.label')} desc={t('push.alerts.desc')}
            checked={prefs?.push_alerts !== false}
            disabled={loading || saving}
            onChange={(v) => onChange({ push_alerts: v })}
          />
          <button
            type="button"
            className={styles.notifRow}
            disabled={busy}
            onClick={() => { void disable(); }}
            style={{
              width: '100%', background: 'none', textAlign: 'left', font: 'inherit', color: 'inherit',
              ...(busy ? { opacity: 0.6, pointerEvents: 'none' } : {}),
            }}
          >
            <div className={styles.notifIcon}><BellOff size={16} /></div>
            <div className={styles.notifInfo}>
              <p className={styles.notifLabel}>{t('push.disableCta')}</p>
              <p className={styles.notifDesc}>{t('push.disableDesc')}</p>
            </div>
          </button>

          {/* Sender identification, deliberately next to the off
              switches. The CASL exemption that makes push workable at
              all asks for the identifying information AND the opt-out
              to be conspicuously published on the interface the message
              is read through — which is this page, not the notification
              body, because a lock-screen line has no room for either.
              Rendering it here is what makes that true rather than
              claimed. The postal address is empty until Warren supplies
              it (docs/tickets/push-sender-identification.md); the line
              is omitted rather than faked. */}
          <div className={styles.notifRow} style={{ display: 'block' }}>
            <p className={styles.notifLabel}>{t('push.identity.title')}</p>
            <p className={styles.notifDesc}>{t('push.identity.body')}</p>
            {t('push.identity.address') && (
              <p className={styles.notifDesc}>{t('push.identity.address')}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A three-state row. Two categories need one — Charging and Your
 * charger — and in both the driver's real complaint is RATE, not
 * subject. Giving them a frequency control keeps the answer in one
 * row instead of splitting the category into extra switches, which
 * is what made the two legacy session toggles two rows expressing
 * one preference.
 */
function ModeRow({
  label, desc, modes, current, optionLabel, onPick, disabled = false,
}: {
  label: string; desc: string;
  modes: readonly string[];
  current: string;
  optionLabel: (mode: string) => string;
  onPick: (mode: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className={styles.notifRow} style={{ display: 'block' }}>
      <p className={styles.notifLabel}>{label}</p>
      <p className={styles.notifDesc}>{desc}</p>
      <div role="radiogroup" aria-label={label} style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        {modes.map((mode) => {
          const on = current === mode;
          return (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={disabled}
              // Always PATCH, even when this mode is already selected.
              // The displayed mode can be DERIVED from the legacy
              // per-event fields rather than stored, and a derived
              // value can disagree with what the emitter will do. A
              // no-op on the current mode left the driver unable to
              // resolve that: tapping what they already see writes the
              // value and makes the two agree.
              onClick={() => onPick(mode)}
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 10, font: 'inherit', fontSize: 13, fontWeight: 600,
                cursor: on ? 'default' : 'pointer',
                border: '1px solid var(--evi-border)',
                background: on ? 'var(--evi-accent)' : 'transparent',
                color: on ? 'var(--evi-on-accent, #fff)' : 'var(--evi-text)',
              }}
            >
              {optionLabel(mode)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NotifToggle({
  icon: Icon, label, desc, checked, onChange, disabled = false,
}: {
  icon: React.ComponentType<{ size: number }>;
  label: string; desc: string;
  checked: boolean; onChange: (v: boolean) => void;
  /** When true, the switch is non-interactive (pending mutation,
   *  initial fetch, etc.). Visual treatment kept minimal — pointer
   *  events disabled + opacity reduced. */
  disabled?: boolean;
}) {
  return (
    <label className={styles.notifRow} style={disabled ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
      <div className={styles.notifIcon}><Icon size={16} /></div>
      <div className={styles.notifInfo}>
        <p className={styles.notifLabel}>{label}</p>
        <p className={styles.notifDesc}>{desc}</p>
      </div>
      <span
        className={`${styles.switch} ${checked ? styles.switchOn : ''}`}
        onClick={() => { if (!disabled) onChange(!checked); }}
      >
        <span className={styles.switchKnob} />
      </span>
    </label>
  );
}
